import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RecoveryBundle, VaultItem } from '@heybesafe/shared';
import { unwrapForRecoveryContact, effectiveWebIdentifiers, ensureUrlScheme } from '@heybesafe/shared';
import { useRecovery } from '../context/RecoveryContext';
import { useAccountKey } from '../context/AccountKeyContext';
import { decryptItem } from '../crypto/crypto';
import { HbsListRow } from '../components/hbs/ListRow';
import { HbsSiteIcon } from '../components/hbs/SiteIcon';
import { HbsCopyMenu } from '../components/hbs/CopyMenu';
import { domainFromUrl } from '../utils/domainFromUrl';
import { candidateFaviconUrls, registrableDomain } from '../utils/faviconUrl';

/**
 * Read-only "borrowed safe" view shown to a recovery contact once a protected
 * user's emergency-access wait has elapsed. The page derives the borrowed
 * vault key by ECIES-unwrapping the wrapped bundle with the viewing user's
 * own account private key, then AES-GCM decrypts each item.
 *
 * Why a slim self-contained page (not a refactored SafePage): SafeContext
 * holds the personal vault key + items lifecycle. Threading a "borrowed" key
 * and alternate fetch source through a 700-line context would balloon scope.
 * This page is read-only by design — no edit/share/delete affordances.
 */
export default function RecoveryReadOnlySafePage(): JSX.Element {
  const { protectedUserId } = useParams<{ protectedUserId: string }>();
  const { inbound, fetchWrappedVaultKey, fetchBorrowedItems, loading } = useRecovery();
  const { privateKey } = useAccountKey();

  const [items, setItems] = useState<VaultItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const relationship = protectedUserId
    ? inbound.find((r) => r.protectedUserId === protectedUserId)
    : undefined;

  const releaseAtMs = relationship?.emergencyReleaseAt
    ? new Date(relationship.emergencyReleaseAt).getTime()
    : NaN;
  const accessLive =
    relationship?.state === 'EMERGENCY_REQUESTED' &&
    Number.isFinite(releaseAtMs) &&
    releaseAtMs <= Date.now();

  useEffect(() => {
    if (!protectedUserId || !accessLive || !privateKey) return;

    let cancelled = false;
    (async () => {
      try {
        // 1. Pull wrapped bundle (base64-encoded JSON of RecoveryBundle).
        const wrappedB64 = await fetchWrappedVaultKey(protectedUserId);
        if (cancelled) return;
        const bundleJson = new TextDecoder().decode(
          Uint8Array.from(atob(wrappedB64), (c) => c.charCodeAt(0)),
        );
        const bundle = JSON.parse(bundleJson) as RecoveryBundle;

        // 2. ECIES-unwrap → 32 raw vault-key bytes.
        const vaultKeyBytes = await unwrapForRecoveryContact(
          bundle, privateKey, 'recovery-vault-key-v1',
        );
        if (cancelled) return;

        // 3. Import as AES-GCM CryptoKey for item decryption.
        const vaultKey = await crypto.subtle.importKey(
          'raw',
          vaultKeyBytes as BufferSource,
          { name: 'AES-GCM' },
          false,
          ['decrypt'],
        );

        // 4. Fetch encrypted items, decrypt each. Skip shared (org) items —
        //    the borrowed wrap only carries the protected user's PERSONAL
        //    vault key. Family keys live on a different envelope path.
        const pull = await fetchBorrowedItems(protectedUserId);
        if (cancelled) return;

        const decrypted: VaultItem[] = [];
        for (const enc of pull.items) {
          if (enc.orgId) continue;
          try {
            const item = await decryptItem<VaultItem>(enc.ciphertext, enc.iv, vaultKey);
            // Hide items the protected user marked sensitive — they're for the
            // owner's eyes only and shouldn't be visible to the recovery
            // contact during emergency access. Same client-trusted enforcement
            // as the rest of the sensitive-display flag; the check has to
            // happen post-decrypt because `sensitive` lives inside the
            // encrypted payload, not in server-visible row metadata.
            if (item.sensitive) continue;
            decrypted.push(item);
          } catch {
            // One bad item shouldn't blow up the whole view.
            console.warn(`Skipping undecryptable borrowed item ${enc.id}`);
          }
        }
        if (!cancelled) setItems(decrypted);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load borrowed safe');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [protectedUserId, accessLive, privateKey, fetchWrappedVaultKey, fetchBorrowedItems]);

  // --- Guard rails ----------------------------------------------------------

  if (!protectedUserId) {
    return <Navigate to="/settings/recovery-contacts" replace />;
  }

  if (loading && !relationship) {
    return (
      <div className="p-8 text-sm text-hbs-text-mute">
        Loading…
      </div>
    );
  }

  if (!relationship) {
    return <Navigate to="/settings/recovery-contacts" replace />;
  }

  if (relationship.state !== 'EMERGENCY_REQUESTED') {
    return (
      <div className="p-8">
        <h1 className="text-xl font-semibold text-hbs-text mb-2">Borrowed safe</h1>
        <p className="text-sm text-hbs-text-mute">
          Emergency access is not yet available for {relationship.protectedEmail}.
        </p>
      </div>
    );
  }

  if (!accessLive) {
    const releaseDate = relationship.emergencyReleaseAt?.slice(0, 10) ?? 'unknown';
    return (
      <div className="p-8">
        <h1 className="text-xl font-semibold text-hbs-text mb-2">Borrowed safe</h1>
        <p className="text-sm text-hbs-text-mute">
          Access opens on {releaseDate}. Check back after that date to view{' '}
          {relationship.protectedEmail}'s safe.
        </p>
      </div>
    );
  }

  if (!privateKey) {
    return (
      <div className="p-8 text-sm text-hbs-text-mute">
        Unlocking your account key…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-semibold text-hbs-text mb-2">Borrowed safe</h1>
        <p className="text-sm text-red-300">Couldn't load {relationship.protectedEmail}'s safe: {error}</p>
      </div>
    );
  }

  if (items === null) {
    return (
      <div className="p-8 text-sm text-hbs-text-mute">
        Decrypting {relationship.protectedEmail}'s safe…
      </div>
    );
  }

  // --- Happy path ----------------------------------------------------------

  return (
    <div className="flex flex-col h-full">
      <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-200 px-6 py-3 text-sm">
        <strong>Viewing {relationship.protectedEmail}'s safe in emergency-access mode.</strong>{' '}
        Read-only — you can copy credentials but cannot edit, add, or delete items.
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <h1 className="text-xl font-semibold text-hbs-text mb-4">
          {relationship.protectedEmail}'s safe
        </h1>
        {items.length === 0 ? (
          <p className="text-sm text-hbs-text-mute">No personal items to display.</p>
        ) : (
          <div className="flex flex-col">
            {items.map((item) => (
              <BorrowedItemRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Inline read-only item row. Modeled after SafeItemRow but without the View
 * button (no detail route exists for borrowed items) and the launch overlay
 * (clicking the row opens the URL when present, same as the personal safe).
 */
function BorrowedItemRow({ item }: { item: VaultItem }): JSX.Element {
  const allIdentifierUrls = effectiveWebIdentifiers(item);
  const launchHref = ensureUrlScheme(allIdentifierUrls[0] ?? item.url ?? '');
  const iconHost = domainFromUrl(launchHref || item.url, '');
  const domain = iconHost ? registrableDomain(iconHost) : item.name;
  const faviconUrls = candidateFaviconUrls(item);

  function handleLaunch(): void {
    if (!launchHref) return;
    window.open(launchHref, '_blank', 'noopener,noreferrer');
  }

  function handleCopyUsername(): void {
    void navigator.clipboard.writeText(item.username ?? '');
  }

  function handleCopyPassword(): void {
    void navigator.clipboard.writeText(item.password ?? '');
  }

  return (
    <HbsListRow
      leading={<HbsSiteIcon domain={domain} faviconUrls={faviconUrls} size={34} />}
      title={item.name}
      supporting={item.username ?? launchHref ?? ''}
      onClick={launchHref ? handleLaunch : undefined}
      actions={
        <HbsCopyMenu
          onCopyUsername={handleCopyUsername}
          onCopyPassword={handleCopyPassword}
          hasUsername={!!item.username}
          hasPassword={!!item.password}
        />
      }
    />
  );
}
